import { useSelector } from "react-redux";
import { ScheduleChangeSeverity } from "@b2bportal/air-booking-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";
import { FlightPostBookingSelectors } from "../../../../features/postbooking/store";
import type { FlightItineraryDetailSliceSummaryProps } from "./types";

export const useFlightItineraryDetailSliceSummary = ({
  slice,
  sliceChangeContext,
}: FlightItineraryDetailSliceSummaryProps) => {
  const { t, formatDateTime, formatInterval } = useI18nContext();

  const airports = useSelector(FlightPostBookingSelectors.getAirports);
  const airlines = useSelector(FlightPostBookingSelectors.getAirlines);

  const { segments } = slice;
  const segmentCount = segments.length;
  const firstSegment = segments[0];
  const lastSegment = segments[segmentCount - 1];

  const originLocation = firstSegment.origin;
  const destinationLocation = lastSegment.destination;
  const origin =
    airports[originLocation.locationCode]?.cityName ??
    originLocation.locationCode;
  const destination =
    airports[destinationLocation.locationCode]?.cityName ??
    destinationLocation.locationCode;

  const departureDateISO =
    firstSegment.updatedDeparture ?? firstSegment.scheduledDeparture;
  const arrivalDateISO =
    firstSegment.updatedArrival ?? firstSegment.scheduledArrival;
  const departureDate = formatDateTime(
    dayjs(departureDateISO).toDate(),
    DateTimeFormatStyle.MediumDate
  );

  const departureTime = formatDateTime(
    dayjs(departureDateISO).toDate(),
    DateTimeFormatStyle.ShortTime
  );
  const arrivalTime = formatDateTime(
    dayjs(arrivalDateISO).toDate(),
    DateTimeFormatStyle.ShortTime
  );

  const { marketingAirline } = firstSegment;
  const airline =
    airlines[marketingAirline.code]?.displayName ?? marketingAirline.code;
  const flightNumber = marketingAirline.flightNumber;

  return {
    context: {
      airlineCode: marketingAirline.code,
      hasMajorScheduleChange:
        sliceChangeContext?.severity === ScheduleChangeSeverity.Major,
      arrivalTime,
      departureTime,
      flightNumber,
      sliceChangeContext,
    },
    text: {
      title: t("core-flights.itinerary.card.itinerary", {
        origin,
        destination,
      }),
      departureDate,
      departureArrival: t("core-flights.itinerary.detail.departureArrival", {
        departure: departureTime,
        arrival: arrivalTime,
      }),
      stops: t("core-flights.itinerary.detail.stops", {
        count: segmentCount - 1,
      }),
      duration: formatInterval(
        dayjs(
          lastSegment.zonedUpdatedArrival ?? lastSegment.zonedScheduledArrival
        ).diff(
          dayjs(
            firstSegment.zonedUpdatedDeparture ??
              firstSegment.zonedScheduledDeparture
          ),
          "minutes"
        )
      ),
      airline,
      scheduleChangeCtaLabel: t(
        "core-flights.itinerary.card.notificationBanner.reviewScheduleChangeBtn"
      ),
    },
  };
};
